import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Button,
  Select,
  Datepicker,
  FormHeader,
  FormActions,
} from 'components';
import { globalPropTypes, checkPermissions } from 'helpers';
import { toApiDate } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import { getIpmaMeetingOptions } from 'api/services/meeting';
import { exportIpmaMeetingApplicationsCreator } from 'store/actionsCreators';
import { flagColumnKeys } from 'containers/ma/Meetings/Application/flag';

/** "all" exports every type the reader is allowed to see. */
// The YC Assembly NDA is only offered to the people allowed to read it.
const MEETING_TYPES = ['all', 'cod', 'csp', 'ycnda', 'attendance'];

/**
 * The attendance forms are called "attendance" on screen and "ipma" by the API,
 * which knows them as a meta meeting type. Everything else is named the same on
 * both sides.
 */
const apiType = (type) => (type === 'attendance' ? 'ipma' : type);

/** Nothing picked: every filter starts here and the reset returns to it. */
const NO_FILTERS = {
  meetingType: 'all',
  meeting: null,
  memberAssociations: [],
  status: '',
  verified: '',
  dateFrom: null,
  dateTo: null,
};

/**
 * The export of the meeting applications, with the filters that decide what
 * ends up in the file.
 *
 * Everything left empty is exported in full, so the screen is usable straight
 * away and narrows down only as much as the user asks for. The status history
 * of every application travels along in the file.
 */
const ExportMeetingApplications = ({ exportApplications, userPermissions }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [filters, setFilters] = useState(NO_FILTERS);
  const [meetings, setMeetings] = useState([]);
  const [memberAssociations, setMemberAssociations] = useState([]);

  const canView = checkPermissions(
    {
      roles: ['ipma_meeting_view_cod', 'ipma_meeting_view_csp', 'ipma_meeting_view_ipma'],
    },
    userPermissions || [],
  );
  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    canView,
    '/ipma',
    'page.ipma.meetings.accessDenied',
  );

  // The meetings of the picked type, so one of them can be exported alone.
  useEffect(() => {
    if (!allowed) return undefined;

    let current = true;

    getIpmaMeetingOptions(apiType(filters.meetingType))
      .then((response) => response.json())
      .then(({ results }) => {
        if (!current) return;

        const result = (results && results.result) || {};

        setMeetings(result.meetings || []);
        setMemberAssociations(result.memberAssociations || []);
      })
      .catch(() => {
        if (!current) return;

        setMeetings([]);
        setMemberAssociations([]);
      });

    return () => {
      current = false;
    };
  }, [filters.meetingType, allowed]);

  const update = (name, value) => setFilters((previous) => ({ ...previous, [name]: value }));

  const typeOptions = MEETING_TYPES.filter(
    (value) =>
      value !== 'ycnda' ||
      checkPermissions({ roles: ['ipma_meeting_view_ycnda'] }, userPermissions || []),
  ).map((value) => ({
    value,
    label:
      value === 'all'
        ? t('page.ipma.meetings.list.exportMeetingTypeAll')
        : t(`navigation.tabs.ipma.meetings.${value}`),
  }));

  const statusOptions = [
    { value: '', label: t('page.ipma.meetings.list.exportStatusPlaceholder') },
    { value: 'SUBMITTED', label: t('page.ma.meetings.list.statusSubmitted') },
    { value: 'CHAIR_APPROVED', label: t('page.ma.meetings.list.statusChairApproved') },
    { value: 'ACCEPTED', label: t('page.ma.meetings.list.statusAccepted') },
    { value: 'REJECTED', label: t('page.ma.meetings.list.statusRejected') },
  ];

  // The one IPMA flag, under the name the picked type gives it: the invoice on
  // a COD export, the verified flag on the others. Only a COD export can hold
  // invoiced seats.
  const flagKeysOfType = flagColumnKeys(
    ['all', 'cod'].includes(filters.meetingType) ? 'cod' : filters.meetingType,
  );
  const verifiedOptions = [
    { value: '', label: t(flagKeysOfType.placeholder) },
    { value: 'PAID', label: t(flagKeysOfType.filterOn) },
    { value: 'UNPAID', label: t(flagKeysOfType.filterOff) },
  ];

  // Whether anything narrows the export down, so the reset is offered only
  // when there is something to undo.
  const isFiltered =
    filters.meetingType !== NO_FILTERS.meetingType ||
    !!filters.meeting ||
    filters.memberAssociations.length > 0 ||
    !!filters.status ||
    !!filters.verified ||
    !!filters.dateFrom ||
    !!filters.dateTo;

  const submit = () => {
    exportApplications(filters.meetingType, {
      meeting: filters.meeting || undefined,
      memberAssociation: filters.memberAssociations.length
        ? filters.memberAssociations.map((option) => option.value)
        : undefined,
      status: filters.status || undefined,
      verified: filters.verified || undefined,
      dateFrom: filters.dateFrom ? toApiDate(filters.dateFrom) : undefined,
      dateTo: filters.dateTo ? toApiDate(filters.dateTo) : undefined,
    });
  };

  const selected = (options, value) => options.find((option) => option.value === value) || '';

  // The meetings come from the API, so the way back to all of them is put in
  // front of them here rather than in the payload.
  const meetingOptions = [
    { value: null, label: t('page.ipma.meetings.list.exportMeetingPlaceholder') },
    ...meetings,
  ];

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <Container>
        <FormHeader.Wrapper>
          <FormHeader.Title>{t('page.ipma.meetings.list.exportTitle')}</FormHeader.Title>
          <FormHeader.Description>
            {t('page.ipma.meetings.list.exportDescription')}
          </FormHeader.Description>
        </FormHeader.Wrapper>
        <Row>
          <Col size={{ xl: 1 / 2 }}>
            <Margin bottom="20">
              <Field id="meetingType" label={t('page.ipma.meetings.list.exportMeetingType')}>
                <Select
                  id="meetingType"
                  options={typeOptions}
                  value={selected(typeOptions, filters.meetingType)}
                  placeholder={t('page.ipma.meetings.list.exportMeetingTypePlaceholder')}
                  onChange={(option) => {
                    // Both lists below belong to the type, so a change of type
                    // leaves no pick of the previous one behind.
                    setFilters((previous) => ({
                      ...previous,
                      meetingType: option ? option.value : 'all',
                      meeting: null,
                      memberAssociations: [],
                    }));
                  }}
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 2 }}>
            <Margin bottom="20">
              <Field id="meeting" label={t('page.ipma.meetings.list.exportMeeting')}>
                <Select
                  id="meeting"
                  options={meetingOptions}
                  value={selected(meetingOptions, filters.meeting)}
                  placeholder={t('page.ipma.meetings.list.exportMeetingPlaceholder')}
                  onChange={(option) => update('meeting', option ? option.value : null)}
                />
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 2 }}>
            <Margin bottom="20">
              <Field
                id="memberAssociations"
                label={t('page.ipma.meetings.list.exportMemberAssociation')}
              >
                <Select
                  id="memberAssociations"
                  isMulti
                  isClearable
                  options={memberAssociations}
                  value={filters.memberAssociations}
                  placeholder={t('page.ipma.meetings.list.exportMemberAssociationPlaceholder')}
                  onChange={(options) => update('memberAssociations', options || [])}
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 4 }}>
            <Margin bottom="20">
              <Field id="status" label={t('page.ipma.meetings.list.exportStatus')}>
                <Select
                  id="status"
                  options={statusOptions}
                  value={selected(statusOptions, filters.status)}
                  placeholder={t('page.ipma.meetings.list.exportStatusPlaceholder')}
                  onChange={(option) => update('status', option ? option.value : '')}
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 4 }}>
            <Margin bottom="20">
              <Field id="verified" label={t(flagKeysOfType.column)}>
                <Select
                  id="verified"
                  options={verifiedOptions}
                  value={selected(verifiedOptions, filters.verified)}
                  placeholder={t(flagKeysOfType.placeholder)}
                  onChange={(option) => update('verified', option ? option.value : '')}
                />
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 4 }}>
            <Margin bottom="20">
              <Field id="dateFrom" label={t('page.ipma.meetings.list.exportDateFrom')}>
                <Datepicker
                  id="dateFrom"
                  selected={filters.dateFrom}
                  onChange={(value) => update('dateFrom', value)}
                  dateFormat="yyyy-MM-dd"
                  placeholderText={t('page.ipma.meetings.list.exportDatePlaceholder')}
                  showYearDropdown
                  showMonthDropdown
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 4 }}>
            <Margin bottom="20">
              <Field id="dateTo" label={t('page.ipma.meetings.list.exportDateTo')}>
                <Datepicker
                  id="dateTo"
                  selected={filters.dateTo}
                  onChange={(value) => update('dateTo', value)}
                  dateFormat="yyyy-MM-dd"
                  placeholderText={t('page.ipma.meetings.list.exportDatePlaceholder')}
                  minDate={filters.dateFrom}
                  showYearDropdown
                  showMonthDropdown
                />
              </Field>
            </Margin>
          </Col>
        </Row>

        <FormActions>
          <Button type="button" style={{ width: 'auto' }} onClick={submit}>
            {t('page.ipma.meetings.list.export')}
          </Button>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            disabled={!isFiltered}
            onClick={() => setFilters(NO_FILTERS)}
          >
            {t('page.ipma.meetings.list.exportReset')}
          </Button>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() =>
              history.push(
                `/ipma/meetings/${filters.meetingType === 'all' ? 'cod' : filters.meetingType}`,
              )
            }
          >
            {t('page.ipma.meetings.list.exportCancel')}
          </Button>
        </FormActions>
      </Container>
    </PageContent>
  );
};

ExportMeetingApplications.propTypes = {
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  exportApplications: PropTypes.func.isRequired,
};

const mapStateToProps = ({ user }) => ({
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  exportApplications: (listType, filters) =>
    dispatch(exportIpmaMeetingApplicationsCreator(listType, filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExportMeetingApplications);
